/**
 * PATH: apps/quote/src/pages/claims/claim-editor-page/evidence/useClaimEvidence.ts
 * FILE_ID: QUOTE-CLAIMS-EVIDENCE-HOOK-001
 * PURPOSE: Own claim evidence query and mutation boundaries.
 * WHY: The editor and send flow must consume one cached manifest.
 */

import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useApi } from '@/lib/setupApi'
import type {
  ClaimEvidence,
  ClaimEvidenceState,
  EvidenceUpdate,
} from './types'

type EvidenceMutation =
  | { kind: 'add'; body: ({ file_record_id: string } | { email_id: string }) & { claim_line_id?: string | null } }
  | { kind: 'upload'; file: File; claimLineId?: string | null }
  | { kind: 'update'; evidenceId: string; body: EvidenceUpdate }
  | { kind: 'remove'; evidenceId: string }
  | { kind: 'reorder'; orderedIds: string[] }

const EMPTY_EVIDENCE: ClaimEvidence[] = []

export function useClaimEvidence(claimId?: string): ClaimEvidenceState {
  const api = useApi()
  const queryClient = useQueryClient()
  const queryKey = useMemo(() => ['claim-evidence', claimId], [claimId])
  const collectionUrl = `/api/claims/${claimId}/evidence/`

  const query = useQuery<ClaimEvidence[]>({
    queryKey,
    queryFn: async () => {
      const response = await api.get<ClaimEvidence[]>(collectionUrl)
      return response.data
    },
    enabled: Boolean(claimId),
  })

  const mutation = useMutation({
    mutationFn: async (input: EvidenceMutation) => {
      if (input.kind === 'add') {
        return (await api.post<ClaimEvidence>(collectionUrl, input.body)).data
      }
      if (input.kind === 'upload') {
        const body = new FormData()
        body.append('file', input.file, input.file.name)
        if (input.claimLineId) body.append('claim_line_id', input.claimLineId)
        return (await api.post<ClaimEvidence>(collectionUrl, body)).data
      }
      if (input.kind === 'update') {
        return (
          await api.patch<ClaimEvidence>(
            `${collectionUrl}${input.evidenceId}/`,
            input.body,
          )
        ).data
      }
      if (input.kind === 'remove') {
        await api.delete(`${collectionUrl}${input.evidenceId}/`)
        return null
      }
      return (
        await api.patch<ClaimEvidence[]>(collectionUrl, {
          ordered_ids: input.orderedIds,
        })
      ).data
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey }),
        queryClient.invalidateQueries({ queryKey: ['claim', claimId] }),
      ])
    },
  })

  return {
    items: query.data ?? EMPTY_EVIDENCE,
    isLoading: query.isLoading,
    error: query.error,
    addFile: async (fileRecordId, claimLineId) => {
      await mutation.mutateAsync({
        kind: 'add',
        body: { file_record_id: fileRecordId, claim_line_id: claimLineId },
      })
    },
    addEmail: async (emailId, claimLineId) => {
      await mutation.mutateAsync({
        kind: 'add',
        body: { email_id: emailId, claim_line_id: claimLineId },
      })
    },
    uploadFile: async (file, claimLineId) => {
      await mutation.mutateAsync({ kind: 'upload', file, claimLineId })
    },
    update: async (evidenceId, values) => {
      await mutation.mutateAsync({
        kind: 'update',
        evidenceId,
        body: values,
      })
    },
    remove: async (evidenceId) => {
      await mutation.mutateAsync({ kind: 'remove', evidenceId })
    },
    reorder: async (orderedIds) => {
      await mutation.mutateAsync({ kind: 'reorder', orderedIds })
    },
    isMutating: mutation.isPending,
  }
}
